import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { loadManualReturnAttempt, manualControlReturnApi, manualReturnReasonText, saveManualReturnAttempt, type ManualControlReturnAttempt, type ManualControlReturnResult } from "../lib/manual-control-return";
import { Button } from "./Button";
import "./ManualControlReturnButton.css";

interface ManualControlReturnButtonProps {
  deviceId: string;
  disabled?: boolean;
  size?: "normal" | "small";
  onReturned?: (result: ManualControlReturnResult) => void | Promise<void>;
}

const errorStatus = (error: unknown) =>
  error && typeof error === "object" && "status" in error && typeof error.status === "number" ? error.status : null;
const validReceipt=(value:unknown,requestId:string):value is ManualControlReturnResult=>{
  if(!value||typeof value!=="object")return false;
  const receipt=value as Record<string,unknown>;
  return receipt.request_id===requestId&&typeof receipt.released==="boolean"&&typeof receipt.resume_started==="boolean"&&typeof receipt.pending_reconciliation==="boolean";
};
export function ManualControlReturnButton({deviceId,disabled=false,size="small",onReturned}:ManualControlReturnButtonProps) {
  const cache=useQueryClient();
  const currentDevice=useRef(deviceId);currentDevice.current=deviceId;
  const attempt=useRef<ManualControlReturnAttempt|null>(loadManualReturnAttempt(deviceId));
  const busy=useRef(false);
  const [phase,setPhase]=useState<"idle"|"preview"|"submit"|"unknown">(attempt.current?"unknown":"idle");
  const [message,setMessage]=useState(attempt.current?"上次交还仍在处理中，再次点击即可继续。":"");

  useEffect(()=>{
    attempt.current=loadManualReturnAttempt(deviceId);
    setPhase(attempt.current?"unknown":"idle");
    setMessage(attempt.current?"上次交还仍在处理中，再次点击即可继续。":"");
  },[deviceId]);

  const refresh=async()=>{
    const results=await Promise.allSettled(["phone-control","device-queues"].map(key=>cache.invalidateQueries({queryKey:[key]})));
    return results.some(result=>result.status==="rejected");
  };
  const submit=async(current:ManualControlReturnAttempt)=>{
    if(current.deviceId!==currentDevice.current){attempt.current=loadManualReturnAttempt(currentDevice.current);setPhase(attempt.current?"unknown":"idle");setMessage("手机已切换，请重新预览交还状态");return;}
    setPhase("submit");
    setMessage("正在交还控制并恢复当前任务…");
    let result:ManualControlReturnResult;
    try{
      const receipt:unknown=await manualControlReturnApi.submit(current.deviceId,{request_id:current.requestId,return_token:current.preview.return_token});
      if(!validReceipt(receipt,current.requestId))throw new Error("交还回执身份或字段未确认");
      result=receipt;
    }catch(error){
      const status=errorStatus(error);
      if(status!==null&&status>=400&&status<500){
        saveManualReturnAttempt(null,current.deviceId);
        if(currentDevice.current!==current.deviceId){await refresh();return;}
        attempt.current=null;
        setPhase("idle");
        setMessage(`交还条件已变化，请重新预览。${manualReturnReasonText(error instanceof Error?error.message:null,"")}`);
      }else{
        saveManualReturnAttempt(current,current.deviceId);
        if(currentDevice.current!==current.deviceId){await refresh();return;}
        attempt.current=current;
        setPhase("unknown");
        setMessage("上次交还仍在处理中，再次点击即可继续。");
      }
      await refresh();
      return;
    }
    if(result.needs_new_preview===true){
      saveManualReturnAttempt(null,current.deviceId);
      if(currentDevice.current!==current.deviceId){await refresh();return;}
      attempt.current=null;setPhase("idle");
      setMessage(manualReturnReasonText(result.reason,"手机或队列状态已变化，请重新预览后交还"));
      if(await refresh())setMessage(currentMessage=>`${currentMessage}；页面状态刷新失败，请手动刷新`);
      return;
    }
    if(result.terminal!==true){
      saveManualReturnAttempt(current,current.deviceId);
      if(currentDevice.current!==current.deviceId){await refresh();return;}
      attempt.current=current;setPhase("unknown");
      setMessage(manualReturnReasonText(result.reason,"交还结果正在核对。请使用原请求核对，不会重复执行原动作。"));
      if(await refresh())setMessage(currentMessage=>`${currentMessage}；页面状态刷新失败，请手动刷新`);return;
    }
    saveManualReturnAttempt(null,current.deviceId);
    if(currentDevice.current!==current.deviceId){await refresh();return;}
    attempt.current=null;setPhase("idle");
    setMessage(result.released
      ? result.resume_started?"已交还系统，正在恢复任务":manualReturnReasonText(result.reason,"已交还系统，当前没有需要立即继续的任务")
      : manualReturnReasonText(result.reason,"系统未确认交还，未继续任务，请重新预览"));
    if(result.released)try{await onReturned?.(result);}catch{setMessage(currentMessage=>`${currentMessage}；页面状态刷新失败，请手动刷新`);}
    if(await refresh())setMessage(currentMessage=>`${currentMessage}；页面状态刷新失败，请手动刷新`);
  };
  const run=async()=>{
    if(busy.current||disabled)return;
    busy.current=true;
    try{
      if(attempt.current){await submit(attempt.current);return;}
      const requestedDevice=deviceId;
      setPhase("preview");
      setMessage("正在核对人工控制和队列状态…");
      try{
        const preview=await manualControlReturnApi.preview(requestedDevice);
        if(currentDevice.current!==requestedDevice)return;
        if(!preview.can_return){setPhase("idle");setMessage(manualReturnReasonText(preview.reason,"当前不能安全交还，请先核对手机状态"));return;}
        if(typeof preview.return_token!=="string"||!preview.return_token.trim()){setPhase("idle");setMessage("交还预览缺少有效凭据，未提交操作，请重新预览");return;}
        const recovered=preview.recovery_active===true;
        const fresh=preview.recovery_active===false&&preview.request_id===null;
        if((recovered&&(typeof preview.request_id!=="string"||!preview.request_id.trim()))||(!recovered&&!fresh)){
          setPhase("idle");setMessage("交还预览缺少可确认的请求身份，未提交操作，请重新预览");return;
        }
        const current={deviceId:requestedDevice,preview,requestId:recovered?preview.request_id as string:crypto.randomUUID()};
        attempt.current=current;
        saveManualReturnAttempt(current,requestedDevice);
        await submit(current);
      }catch(error){
        if(currentDevice.current!==requestedDevice)return;
        setPhase("idle");
        setMessage(error instanceof Error?error.message:"无法读取交还预览，请稍后重试");
      }
    }finally{busy.current=false;}
  };
  const loading=phase==="preview"||phase==="submit";
  return <span className="manual-control-return">
    <Button size={size} variant="primary" disabled={disabled} loading={loading} onClick={()=>void run()}>交还系统并继续</Button>
    {message?<small role="status">{message}</small>:null}
  </span>;
}
